import { MoreHorizontal } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { ProjectColorBar } from '@/components/ColorSwatch';
import { ShiftBadge } from '@/components/shifts/ShiftBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useDeleteEntry } from '@/hooks/use-entries';
import { cn } from '@/lib/cn';
import { formatClock, formatDurationHm } from '@/lib/format';
import type { EntryListItem } from '@/types/entries';

interface EntryCardProps {
  entry: EntryListItem;
  onEdit: (entry: EntryListItem) => void;
}

/**
 * One entry row on the Time log (architecture sprint-03 §9.6, fixes INV #17; sprint 7 original flat row).
 * The actions menu is always visible: there are no hover-only controls (RD §3.5 CHK D7). The end time is not shown because the stored DTO end
 * is DST-naive (§3.2), and origin is never shown.
 */
export function EntryCard({ entry, onEdit }: EntryCardProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const remove = useDeleteEntry();
  const noRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (confirming) noRef.current?.focus();
  }, [confirming]);

  function onConfirmKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      setConfirming(false);
    }
  }

  function confirmDelete(): void {
    remove.mutate(entry.id, {
      onSuccess: () => {
        toast.success('Entry deleted.');
      },
    });
  }

  const project = entry.project;
  return (
    <div className={cn('flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-accent', remove.isPending && 'pointer-events-none opacity-50')}>
      <ProjectColorBar hex={project?.color} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{project ? project.name : 'No project'}</span>
          {project?.status === 'archived' && <Badge>Archived</Badge>}
          {entry.shiftId && <ShiftBadge />}
        </div>
        {entry.description && <p className="mt-0.5 line-clamp-2 break-words text-sm text-foreground">{entry.description}</p>}
      </div>
      <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{formatClock(entry.startSecond)}</span>
      <span className="shrink-0 font-mono text-sm tabular-nums text-foreground">{formatDurationHm(entry.durationSeconds)}</span>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-1" onKeyDown={onConfirmKeyDown}>
          <span className="text-xs text-muted-foreground">Delete?</span>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={remove.isPending}
            className="rounded px-2 py-2 text-xs font-medium text-danger-fg hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:py-1"
          >
            Yes
          </button>
          <button
            ref={noRef}
            type="button"
            onClick={() => setConfirming(false)}
            disabled={remove.isPending}
            className="rounded px-2 py-2 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:py-1"
          >
            No
          </button>
        </div>
      ) : entry.editable === false || entry.shiftId ? null : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Entry actions"
              className="-my-2 h-11 w-11 shrink-0 text-muted-foreground sm:my-0 sm:h-7 sm:w-7 [&_svg]:size-3.5"
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit(entry)}>Edit</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setConfirming(true)}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
